import { FC, useEffect, useRef, useState } from "react";
import Logo from "shared/Logo/Logo";
import HeroSearchFormSmall, {
  SearchTab,
} from "components/HeroSearchFormSmall/HeroSearchFormSmall";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SwitchDarkMode from "shared/SwitchDarkMode/SwitchDarkMode";
import AvatarDropdown from "./AvatarDropdown";
import HeroSearchForm2MobileFactory from "components/HeroSearchForm2Mobile/HeroSearchForm2MobileFactory";
import { StaySearchFormFields } from "components/HeroSearchForm/type";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import {
  getCurrentUser,
  getUnreadChatCount,
} from "../../redux/reducers/UserSlice/UserSlice";
import { useSelector } from "react-redux";
import { logout } from "../../redux/reducers/AuthSlice/AuthSlice";
import { getAllProperties } from "../../redux/reducers/Properties/PropertiesSlice";
import { isAuthenticated } from "utils/helpers/CommonHelper";

interface Header3Props {
  className?: string;
}

let WIN_PREV_POSITION = window.pageYOffset;

const Header3: FC<Header3Props> = ({ className = "" }) => {
  const headerInnerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { authenticated } = useSelector((state: RootState) => state.auth);
  const { currentUser } = useSelector((state: RootState) => state.user);
  const unreadChatCount = useSelector(
    (state: RootState) => state.user.unreadChatCount
  );
  //
  const [showHeroSearch, setShowHeroSearch] =
    useState<StaySearchFormFields | null>();
  //
  const [currentTab, setCurrentTab] = useState<SearchTab>("Stays");

  //
  // Custom outside click handler that ignores Google Autocomplete
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if click was on Google Autocomplete dropdown
      const pacContainer = document.querySelector('.pac-container');
      if (pacContainer && pacContainer.contains(event.target as Node)) {
        return; // Don't close if clicking on autocomplete dropdown
      }
      
      if (headerInnerRef.current && !headerInnerRef.current.contains(event.target as Node)) {
        setShowHeroSearch(null);
        setCurrentTab("Stays");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const location = useLocation();
  //

  useEffect(() => {
    if (showHeroSearch !== null) {
      setShowHeroSearch(null);
    }
  }, [location?.pathname]);

  // HIDDEN WHEN SCROLL EVENT
  useEffect(() => {
    window.addEventListener("scroll", handleEvent);
    return () => {
      window.removeEventListener("scroll", handleEvent);
    };
  }, []);

  const handleEvent = () => {
    window.requestAnimationFrame(handleHideSearchForm);
  };

  const handleHideSearchForm = () => {
    if (!document.querySelector("#nc-Header-3-anchor")) {
      return;
    }
    //
    let currentScrollPos = window.pageYOffset;
    if (
      WIN_PREV_POSITION - currentScrollPos > 100 ||
      WIN_PREV_POSITION - currentScrollPos < -100
    ) {
      setShowHeroSearch(null);
    } else {
      return;
    }
    WIN_PREV_POSITION = currentScrollPos;
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  useEffect(() => {
    if (authenticated) {
      dispatch(getCurrentUser());
      dispatch(getAllProperties(1));
    }
  }, [authenticated]);

  const handleAuthRouting = async () => {
    if (authenticated && currentUser) {
      if (!currentUser?.email_verified) {
        navigate("/account");
      } else if (currentUser?.is_host && !currentUser?.host_verified) {
        navigate("/account");
      } else if (!currentUser?.profile_complete) {
        navigate("/account");
      }
    }
  };

  useEffect(() => {
    setTimeout(() => {
      handleAuthRouting();
    }, 200);
  }, [location?.pathname, authenticated, currentUser]);

  useEffect(() => {
    if (isAuthenticated() && currentUser) {
      dispatch(getUnreadChatCount());
    }
  }, [dispatch, currentUser]);

  useEffect(() => {}, [unreadChatCount]);

  //
  const renderHeroSearch = () => {
    return (
      <div
        className={`absolute inset-x-0 top-0 transition-all will-change-[transform,opacity] ${
          showHeroSearch
            ? "visible"
            : "-translate-x-0 -translate-y-[90px] scale-x-[0.395] scale-y-[0.6] opacity-0 invisible pointer-events-none"
        }`}
      >
        {/* Constrain width to prevent overlap */}
        <div className={`w-full max-w-3xl mx-auto pb-6 px-4`}>
          <HeroSearchFormSmall
            defaultFieldFocus={showHeroSearch || undefined}
            onTabChange={setCurrentTab}
            defaultTab={currentTab}
            onClose={() => setShowHeroSearch(null)}
          />
        </div>
      </div>
    );
  };

  const renderButtonOpenHeroSearch = () => {
    return (
      <div
        className={`w-full max-w-lg relative flex items-center justify-between border-2 border-gray-200 dark:border-neutral-700 rounded-full shadow-sm hover:shadow-lg transition-all duration-200 bg-white dark:bg-neutral-800 ${
          showHeroSearch
            ? "-translate-x-0 translate-y-20 scale-x-[2.55] scale-y-[1.8] opacity-0 pointer-events-none invisible"
            : "visible"
        }`}
      >
        {/* Left side - Location text */}
        <div 
          onClick={() => setShowHeroSearch("location")}
          className="flex-1 flex items-center gap-3 pl-6 pr-4 py-3.5 cursor-pointer group"
        >
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-50 dark:bg-neutral-700 group-hover:bg-[#FEF3F0] dark:group-hover:bg-[#F56948]/20 transition-colors">
            <svg 
              className="w-5 h-5 text-gray-600 dark:text-gray-300 group-hover:text-[#F56948] transition-colors" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" 
              />
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" 
              />
            </svg>
          </div>
          <div className="flex flex-col items-start">
            <span className="text-sm font-semibold text-gray-900 dark:text-white">
              Where to?
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Search neighborhoods
            </span>
          </div>
        </div>

        {/* Vertical divider */}
        <div className="h-10 w-px bg-gray-200 dark:bg-neutral-700" />

        {/* Right side - Search button */}
        <div
          className="flex-shrink-0 px-2 cursor-pointer"
          onClick={() => setShowHeroSearch("location")}
        >
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#F56948] hover:bg-[#e55a3c] shadow-md hover:shadow-lg transition-all duration-200">
            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24">
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
              ></path>
            </svg>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        className={`nc-Header nc-Header-3 fixed z-40 top-0 inset-0 bg-black/30 dark:bg-black/50 transition-opacity will-change-[opacity] ${
          showHeroSearch ? "visible" : "invisible opacity-0 pointer-events-none"
        }`}
      ></div>
      {showHeroSearch && <div id="nc-Header-3-anchor"></div>}
      <header ref={headerInnerRef} className={`sticky top-0 z-40 ${className}`}>
        <div
          className={`bg-white dark:bg-neutral-900 absolute h-full inset-x-0 top-0 transition-transform will-change-[transform,opacity]
          ${showHeroSearch ? "duration-75" : ""} 
          ${
            showHeroSearch
              ? currentTab === "Cars" || currentTab === "Flights"
                ? "scale-y-[4.4]"
                : "scale-y-[3.4]"
              : ""
          }`}
        ></div>
        <div className="relative px-4 lg:container h-[88px] flex">
          <div className="flex-1 flex items-center justify-between">
            {/* Logo (lg+) */}
            <div className="relative z-10 hidden md:flex flex-1">
              <Logo />
            </div>
            <div className="flex-[2] lg:flex-none mx-auto">
              <div className="hidden lg:block">
                {renderButtonOpenHeroSearch()}
              </div>
              <div className="lg:hidden w-full max-w-lg mx-auto">
                <HeroSearchForm2MobileFactory />
              </div>
              {renderHeroSearch()}
            </div>

            {/* NAV */}
            <div className="hidden md:flex relative z-10 flex-1 items-center justify-end text-neutral-700 dark:text-neutral-100">
              <div className="items-center flex space-x-1">
                {authenticated ? (
                  <button
                    // to="#"
                    onClick={handleLogout}
                    className="xl:inline-flex text-opacity-90
                  group px-4 py-2 border border-neutral-300 hover:border-neutral-400 dark:border-neutral-700 rounded-full items-center text-sm text-gray-700 dark:text-neutral-300 font-medium hover:text-opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75"
                  >
                    Logout
                  </button>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="
                 text-opacity-90
                group px-4 py-2 border border-neutral-300 hover:border-neutral-400 dark:border-neutral-700 rounded-full items-center text-sm text-gray-700 dark:text-neutral-300 font-medium hover:text-opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75"
                    >
                      Login
                    </Link>
                    <Link
                      to="/signup"
                      className="
                 text-opacity-90
                group px-4 py-2 border border-neutral-300 hover:border-neutral-400 dark:border-neutral-700 rounded-full items-center text-sm text-gray-700 dark:text-neutral-300 font-medium hover:text-opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75"
                    >
                      Sign up
                    </Link>
                  </>
                )}

                <div></div>
                <SwitchDarkMode />
                {/* <div className="pr-1.5">
                  <NotifyDropdown className="-ml-2 xl:-ml-1" />
                </div> */}
                {/* {authenticated && <AvatarDropdown />} */}

                {authenticated && (
                  <div className="relative">
                    <AvatarDropdown />
                    {unreadChatCount > 0 && (
                      <div className="absolute -top-2 -right-2 flex items-center justify-center min-w-5 h-5 px-1 text-xs text-white bg-red-500 rounded-full">
                        {unreadChatCount > 20 ? "20+" : unreadChatCount}
                      </div>
                    )}
                  </div>
                )}
                {/* <div className="hidden md:block">
                  <MenuBar />
                </div> */}
              </div>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header3;